import React, { useMemo, useState, useEffect, useRef } from 'react';
import AsyncSelect from 'react-select/async';
import { SingleValue, Options, StylesConfig } from 'react-select';
import debounce from 'lodash/debounce';
import { AddressAutocompleteProps } from '@src/v3/interfaces/components/addressAutocomplete';
import { searchProperty } from '@src/v3/services/properties';

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  onSelect,
  defaultInputValue,
  value = '',
  restrict = 'exact_address',
  isAllowGlobalSearch = false,
  isError = false,
  helperText = '',
  wrapperClassName,
  placeholder = 'Enter Company Address',
  disabled = false,
  id,
  showErrorPlaceholder = true,
}) => {
  // State declarations
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [inputValue, setInputValue] = useState<string>(
    value || defaultInputValue || ''
  );
  const [options, setOptions] = useState<{ label: string; value: string }[]>(
    []
  );
  const [isDesktop, setIsDesktop] = useState<boolean>(false);

  // Minimal styles to override react-select inline styles (only for sign-up variant)
  const isAddressAutocompleteWrapper = Boolean(
    wrapperClassName?.includes('address-autocomplete-wrapper')
  );

  // update
  useMemo(() => {
    if (value) {
      setInputValue(value);
    } else if (defaultInputValue) {
      setInputValue(defaultInputValue);
    }
  }, [value, defaultInputValue]);

  const loadOptions = async (
    inputValue: string,
    // eslint-disable-next-line no-unused-vars
    callback: (options: Options<{ label: string; value: string }>) => void
  ): Promise<void> => {
    if (inputValue.length >= 3) {
      setIsLoading(true);

      try {
        const result = await searchProperty(
          inputValue,
          restrict,
          isAllowGlobalSearch,
          ''
        );

        if (result && result.length > 0) {
          const options = result.map(
            (item: { data: string; display: string }) => ({
              label: item.display,
              value: item.data,
            })
          );
          setOptions(options);
          callback(options);
          return;
        }

        callback([]);
      } catch (e) {
        // Error handled
        callback([]);
      } finally {
        setIsLoading(false);
      }
    } else {
      callback([]);
    }
  };

  const debouncedLoadOptions = debounce(
    (
      inputValue: string,
      callback: (
        // eslint-disable-next-line no-unused-vars
        options: Options<{ label: string; value: string }>
      ) => void | Promise<Options<{ label: string; value: string }>>
    ) => {
      loadOptions(inputValue, callback);
    },
    300
  );

  const handleInputChange = (inputValue: string) => {
    setInputValue(inputValue);
    debouncedLoadOptions(inputValue, (suggestions) => {
      setOptions(suggestions.slice()); // Convert to a mutable array
    });
  };

  const handleChange = (
    selectedOption: SingleValue<{ label: string; value: string }>
  ) => {
    const address = selectedOption
      ? {
          address: selectedOption.label,
          place_id: selectedOption.value,
        }
      : null;
    onSelect(address);
    setOptions([]);

    setTimeout(() => {
      if (document?.activeElement) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        document?.activeElement?.blur();
      }
    }, 100);
  };

  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  // Helper function to get border color using switch case for better performance
  // Uses Tailwind CSS variables defined in globals.css and tailwind.config.js
  const getBorderColor = ({
    isError,
    isFocused,
    isAddressAutocompleteWrapper,
  }: {
    isError: boolean;
    isFocused: boolean;
    isAddressAutocompleteWrapper: boolean;
  }) => {
    switch (true) {
      case isError:
        return 'rgb(var(--color-red-700))';

      case isFocused:
        return 'hsl(var(--foreground))';

      case isAddressAutocompleteWrapper:
        return 'rgb(var(--color-gray-300))';

      default:
        return 'rgb(var(--color-gray-400))';
    }
  };

  const signUpStyles:
    | StylesConfig<{ label: string; value: string }, false>
    | undefined = wrapperClassName
    ? {
        control: (base, state) => ({
          ...base,
          borderColor: getBorderColor({
            isError,
            isFocused: state.isFocused,
            isAddressAutocompleteWrapper,
          }),
          backgroundColor: disabled
            ? 'rgb(var(--color-gray-100))'
            : 'rgb(var(--color-background-100))',
          boxShadow: 'none',
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? 'not-allowed' : 'default',
          ...(isAddressAutocompleteWrapper && {
            height: isDesktop ? '56px' : '48px',
            minHeight: isDesktop ? '56px' : '48px',
          }),
          '&:hover': {
            borderColor: getBorderColor({
              isError,
              isFocused: state.isFocused,
              isAddressAutocompleteWrapper,
            }),
          },
        }),
        valueContainer: (base) =>
          isAddressAutocompleteWrapper
            ? {
                ...base,
                height: isDesktop ? '56px' : '48px',
              }
            : base,
        placeholder: (base) =>
          isAddressAutocompleteWrapper
            ? {
                ...base,
                color: isError
                  ? 'rgb(var(--color-red-700))'
                  : 'rgb(var(--color-gray-800))',
                fontSize: '14px !important',
                lineHeight: '20px',
                letterSpacing: '-0.14px',
              }
            : {
                ...base,
                color:
                  isError && showErrorPlaceholder
                    ? 'rgb(var(--color-red-700)) !important'
                    : 'rgb(var(--color-gray-800)) !important',
              },
        singleValue: (base) =>
          isAddressAutocompleteWrapper
            ? {
                ...base,
                color: disabled
                  ? 'rgb(var(--color-gray-600))'
                  : 'rgb(var(--color-gray-800))',
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '-0.14px',
              }
            : {
                ...base,
                color: disabled ? 'rgb(var(--color-gray-600))' : base.color,
              },
        input: (base) =>
          isAddressAutocompleteWrapper
            ? {
                ...base,
                margin: 0,
                padding: 0,
                color: 'rgb(var(--color-gray-800))',
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '-0.14px',
              }
            : base,
      }
    : undefined;

  return (
    <>
      <div
        ref={wrapperRef}
        id={id}
        tabIndex={-1}
        onFocus={() => {
          // ScrollToError may focus this wrapper (because it has an id/data-name match).
          // Forward focus to the actual react-select input so the caret appears.
          const input =
            wrapperRef.current?.querySelector('input') ??
            (id ? document.getElementById(id)?.querySelector('input') : null);
          // Use preventScroll to avoid unexpected jumps.
          (input as HTMLInputElement | null)?.focus?.({ preventScroll: true });
        }}
        className={`${wrapperClassName || ''} ${
          isError ? 'zm-select-error' : ''
        } ${disabled ? 'pointer-events-none' : ''}`}
        style={disabled ? { pointerEvents: 'none' } : undefined}
      >
        <AsyncSelect
          loadOptions={debouncedLoadOptions}
          onInputChange={handleInputChange}
          onChange={handleChange}
          placeholder={placeholder}
          isLoading={isLoading}
          loadingMessage={() => 'Loading...'}
          options={options}
          isClearable={true}
          defaultInputValue={defaultInputValue}
          inputValue={inputValue}
          className={
            wrapperClassName
              ? 'text-base leading-6 tracking-tight font-normal text-gray-1000'
              : `zm-select-wrap ${isError ? 'zm-select-error' : ''}`
          }
          classNamePrefix="zm-select"
          instanceId="address-autocomplete"
          blurInputOnSelect={wrapperClassName ? false : undefined}
          styles={wrapperClassName ? signUpStyles : undefined}
          // menuIsOpen={true}
        />
      </div>

      {helperText ? (
        <span
          className={`block text-sm font-normal ${
            wrapperClassName ? 'mt-1.5 text-red-700' : 'my-1 text-error'
          }`}
        >
          {helperText}
        </span>
      ) : null}
    </>
  );
};

export default AddressAutocomplete;
